import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  TableCell,
  Badge,
  Tooltip,
  Chip,
  Checkbox,
  alpha,
} from "@mui/material";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import BusinessIcon from "@mui/icons-material/Business";
import CloseIcon from "@mui/icons-material/Close";
import CopyButton from "bundles/Internal/components/common/CopyButton";
import RecruiterTableFilters from "bundles/Internal/components/recruiter/RecruiterTableFilters";
import DataTable from "bundles/Internal/components/common/DataTable";

export const COLUMNS_CONSTANTS = [
  { key: "id", label: "Id" },
  { key: "godview_url", label: "Godview" },
  { key: "first_name", label: "First name" },
  { key: "last_name", label: "Last name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "company_name", label: "Company" },
  { key: "job_title", label: "Job title" },
  { key: "preferred_locale", label: "Preferred locale" },
  { key: "created_at", label: "Created at" },
  { key: "active", label: "Active" },
  { key: "password_generated", label: "Password generated" },
  { key: "signup_origin", label: "Signup origin" },
  { key: "super_user", label: "Super user" },
  { key: "linked_recruiter_ids", label: "Linked recruiter ids" },
  { key: "utm_source", label: "Utm source" },
  { key: "sign_in_count", label: "Sign in count" },
  { key: "deleted_at", label: "Deleted at" },
];

export const SIGNUP_ORIGIN_COLOR = {
  form: { border: "#079344", bg: "rgba(7,147,68,0.10)", text: "#079344" },
  landbot: { border: "#6A1B9A", bg: "rgba(106,27,154,0.10)", text: "#6A1B9A" },
  invitation: { border: "#1976D2", bg: "rgba(25,118,210,0.10)", text: "#1976D2" },
};

export const PREFERRED_LOCALE_COLOR = {
  en: { border: "#1976D2", bg: "rgba(25,118,210,0.10)", text: "#1976D2" },
  fr: { border: "#079344", bg: "rgba(7,147,68,0.10)", text: "#079344" },
  nl: { border: "#F57C00", bg: "rgba(245,124,0,0.10)", text: "#F57C00" },
};

const pill = (label, c) => (
  <Chip
    label={String(label).replaceAll("_", " ")}
    size="small"
    sx={{
      backgroundColor: c.bg,
      color: c.text,
      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
      fontSize: 11,
      fontWeight: 600,
      borderRadius: "8px",
      height: 24,
    }}
  />
);

const formatValue = (value) => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("fr-BE", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
};

const DATE_KEYS = new Set([
  "created_at",
  "deleted_at",
]);

function formatRecruiterCell(recruiter, key) {
  const value = recruiter?.[key];

  if (key === "active" || key === "super_user" || key === "password_generated") {
    if (value) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Checkbox
            checked
            disabled
            size="small"
            sx={{ color: "#079344 !important", p: 1 }}
          />
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 1 }}>
        <CloseIcon fontSize="small" sx={{ color: "#d32f2f", fontSize: "20px" }} />
      </Box>
    );
  }

  if (DATE_KEYS.has(key)) {
    return formatDateTime(value);
  }

  if (key === "signup_origin" && value) return pill(value, SIGNUP_ORIGIN_COLOR[value] || SIGNUP_ORIGIN_COLOR.form);
  if (key === "preferred_locale" && value) return pill(value, PREFERRED_LOCALE_COLOR[value] || SIGNUP_ORIGIN_COLOR.form);

  if (key === "email" || key === "phone") {
    if (!value) return "";
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0, maxWidth: 200 }}>
        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: 1 }}>{value}</Box>
        <CopyButton value={value} sx={{ flexShrink: 0 }} />
      </Box>
    );
  }

  if (key === "godview_url") {
    if (!value) return "";
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#079344", fontWeight: 600, textDecoration: "none" }}
        onMouseOver={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
        onMouseOut={(e) => { e.currentTarget.style.textDecoration = "none"; }}
        onClick={(e) => e.stopPropagation()}
      >
        Godview ↗
      </a>
    );
  }

  return formatValue(value);
}

const RecruiterTable = ({
  recruiters = [],
  onRecruiterSelect,
  onOpenNotes,
  onQueryChange,
  filters,
  onFiltersChange,
  page,
  totalCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  loading = false,
  selectable = false,
  selectedRows = [],
  onRowSelect,
  onExportCsv,
}) => {
  const navigate = useNavigate();
  const columns = useMemo(() => COLUMNS_CONSTANTS, []);

  const renderCell = useCallback((recruiter, key) => {
    if (key === "company_name") {
      const value = recruiter?.company_name;
      if (!value) return "";
      return (
        <Box
          component="span"
          onClick={(e) => {
            e.stopPropagation();
            if (recruiter.company_id) navigate(`/internal/companies/${recruiter.company_id}`);
          }}
          sx={{
            color: "#079344",
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {value}
        </Box>
      );
    }
    return formatRecruiterCell(recruiter, key);
  }, [navigate]);

  return (
    <DataTable
      title="Recruiters"
      totalLabel={(count) => `${count.toLocaleString()} total recruiters`}
      emptyIcon={BusinessIcon}
      emptyTitle="No recruiters found"
      emptySubtitle="Try adjusting your search criteria"
      columns={columns}
      data={recruiters}
      renderCell={renderCell}
      extraHeadColumns={["Notes"]}
      renderExtraCells={(recruiter) => {
        const notesCount = Number(recruiter?.notes_count || 0);
        return (
          <TableCell key="notes" sx={{ whiteSpace: "nowrap", py: 1.5 }}>
            <Tooltip title="View notes" arrow placement="top">
              <IconButton
                size="small"
                aria-label="Open notes"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenNotes?.(recruiter);
                }}
                disabled={!onOpenNotes}
                sx={{
                  backgroundColor:
                              notesCount > 0
                                ? alpha("#079344", 0.1)
                                : "transparent",
                  "&:hover": {
                    backgroundColor: alpha("#079344", 0.2),
                  },
                }}
              >
                <Badge
                  badgeContent={notesCount}
                  color="primary"
                  invisible={notesCount === 0}
                  overlap="circular"
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor: "#079344",
                      fontWeight: 600,
                      fontSize: 10,
                    },
                  }}
                >
                  <NoteAddOutlinedIcon
                    fontSize="small"
                    sx={{
                      color:
                                  notesCount > 0 ? "#079344" : "text.secondary",
                    }}
                  />
                </Badge>
              </IconButton>
            </Tooltip>
          </TableCell>
        );
      }}
      onRowClick={onRecruiterSelect}
      onQueryChange={onQueryChange}
      searchPlaceholder="Search by name, email, company, phone, job title..."
      searchInfo="Full name, first name, last name, email, company, job title, phone"
      searchById
      filtersComponent={
        filters && onFiltersChange ? (
          <RecruiterTableFilters filters={filters} onFiltersChange={onFiltersChange} />
        ) : null
      }
      page={page}
      totalCount={totalCount}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      loading={loading}
      selectable={selectable}
      selectedRows={selectedRows}
      onRowSelect={onRowSelect}
      onExportCsv={onExportCsv}
    />
  );
};

export default RecruiterTable;
